'use client';

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { getApiBaseUrl } from "@/lib/apiBaseUrl";
import { DONATION_HEADER_URL, DONATION_SECTION_URL } from "@/lib/donationUrls";

function calendarEventImageSrc(url: string) {
  const s = (url ?? "").trim();
  if (!s) return "";
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  return `${getApiBaseUrl()}${s.startsWith("/") ? s : `/${s}`}`;
}

/** Clave YYYY-MM-DD para comparar fechas locales (año, mes 0-11, día). */
function formatKey(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** date es YYYY-MM-DD del API: evitar new Date("YYYY-MM-DD") (UTC) que desplaza el día en local. */
function formatCalendarDayHeading(dateStr: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((dateStr ?? "").trim());
  if (!m) return dateStr;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  return new Date(y, mo, d).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "long",
  });
}

type CalendarEvent = {
  id: number;
  date: string; // YYYY-MM-DD
  title: string;
  description: string;
  imageUrl: string;
};

type CarouselItem = {
  id: number;
  imageUrl: string;
  title?: string | null;
};

type StoredUser = {
  id?: number;
  email?: string;
  name?: string | null;
  lastName?: string | null;
};

function youtubeUrlToEmbed(url: string): string | null {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  const watchMatch = trimmed.match(/(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/);
  if (watchMatch) return `https://www.youtube.com/embed/${watchMatch[1]}`;
  const shortMatch = trimmed.match(/(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (shortMatch) return `https://www.youtube.com/embed/${shortMatch[1]}`;
  const embedMatch = trimmed.match(/(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if (embedMatch) return trimmed;
  return null;
}

const fallbackCarouselImages = [
  "/images/Diferencia-entre-jardin.jpg",
  "/images/ninos-en-la-escuela.jpg",
  "/images/ninos-lego.jpg",
  "/images/nissos-en-clase.jpg",
];

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [heroVideoUrl, setHeroVideoUrl] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [calendarError, setCalendarError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null);
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  /** Solo en el cliente: evita hydration mismatch si el servidor y el navegador usan distinta fecha local (p. ej. UTC vs zona del usuario). */
  const [clientTodayKey, setClientTodayKey] = useState<string | null>(null);

  const router = useRouter();

  useEffect(() => {
    const t = new Date();
    setClientTodayKey(formatKey(t.getFullYear(), t.getMonth(), t.getDate()));
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/hero-video`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.youtubeUrl) return;
        const embed = youtubeUrlToEmbed(data.youtubeUrl);
        if (embed) setHeroVideoUrl(embed);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetch(`${getApiBaseUrl()}/carousel`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data: CarouselItem[] | null) => {
        if (cancelled || !Array.isArray(data)) return;
        setCarouselItems(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth() + 1; // 1-12
    setCalendarLoading(true);
    setCalendarError(null);
    fetch(`${getApiBaseUrl()}/calendar/events?year=${year}&month=${month}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = await res.json().catch(() => null);
          const msg =
            (data && typeof data.error === "string" && data.error) ||
            `No se pudieron cargar los eventos (${res.status}).`;
          throw new Error(msg);
        }
        return res.json();
      })
      .then((data: CalendarEvent[]) => {
        if (cancelled) return;
        setCalendarEvents(Array.isArray(data) ? data : []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCalendarEvents([]);
        setCalendarError(err instanceof Error ? err.message : "No se pudieron cargar los eventos.");
      })
      .finally(() => {
        if (!cancelled) setCalendarLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [calendarMonth]);

  const goToLoginWithMode = (mode: "login" | "register") => {
    router.push(mode === "register" ? "/login?mode=register" : "/login");
  };

  const goToMaterials = () => {
    if (typeof window === "undefined") return;
    const token = localStorage.getItem("gp_token");
    if (token) {
      router.push("/materiales-educativos");
    } else {
      router.push("/login");
    }
  };

  const goToLogin = () => {
    router.push("/login");
  };

  const effectiveCarouselImages =
    carouselItems.length > 0
      ? carouselItems.map((item) =>
          item.imageUrl.startsWith("http")
            ? item.imageUrl
            : `${getApiBaseUrl()}${item.imageUrl}`,
        )
      : fallbackCarouselImages;

  // Carrusel: auto-avance cada 3 segundos en loop infinito
  useEffect(() => {
    if (effectiveCarouselImages.length <= 1) return;
    const id = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % effectiveCarouselImages.length);
    }, 3000);
    return () => clearInterval(id);
  }, [effectiveCarouselImages.length]);

  const displayedYear = calendarMonth.getFullYear();
  const displayedMonth = calendarMonth.getMonth();

  const calendarMonthLabel = calendarMonth.toLocaleString("es-AR", {
    month: "long",
    year: "numeric",
  });

  const firstDayOfWeek = new Date(displayedYear, displayedMonth, 1).getDay(); // 0 = domingo
  const daysInMonth = new Date(displayedYear, displayedMonth + 1, 0).getDate();
  const daysArray = Array.from({ length: daysInMonth }, (_, idx) => idx + 1);

  const eventsByDate = calendarEvents.reduce<Record<string, CalendarEvent>>((acc, ev) => {
    acc[ev.date] = ev;
    return acc;
  }, {});

  const selectedKey =
    selectedDate != null
      ? formatKey(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
        )
      : calendarEvents.find((ev) =>
          ev.date.startsWith(
            `${displayedYear}-${String(displayedMonth + 1).padStart(2, "0")}`,
          ),
        )?.date ?? null;

  const selectedEvent = selectedKey ? eventsByDate[selectedKey] : null;

  const goToPrevMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  return (
    <div className="min-h-screen bg-[#E9F8D7] text-[#1F2933]">
      {/* Navbar */}
      <header className="bg-[#9B4BB9] text-white shadow-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6">
          {/* Logo / Marca */}
          <div className="flex items-center gap-3">
            <Image
              src="/images/Logo-loop-gp-escuelas.gif"
              alt="Greenpeace en las escuelas"
              width={140}
              height={40}
              priority
              unoptimized
              className="h-auto w-[140px] object-contain md:w-[160px]"
            />
          </div>

          {/* Navegación desktop */}
          <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
            <button
              type="button"
              className="border-b-2 border-white/90 pb-1"
              onClick={() => router.push("/conoce-mas#programa")}
            >
              EL PROGRAMA
            </button>
            <button
              className="pb-1 hover:border-b-2 hover:border-white/70"
              type="button"
              onClick={goToMaterials}
            >
              MATERIALES EDUCATIVOS
            </button>
            <a
              href="#contacto"
              className="pb-1 hover:border-b-2 hover:border-white/70"
            >
              CONTACTO
            </a>
          </nav>

          {/* CTA + menú móvil */}
          <div className="flex items-center gap-3">
            {/* Icono login */}
            <button
              type="button"
              onClick={goToLogin}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xs font-semibold hover:bg-white/20"
              aria-label="Iniciar sesión"
            >
              🔒
            </button>

            <a
              href={DONATION_HEADER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden rounded-full bg-[#FF8A3D] px-4 py-2 text-xs font-semibold uppercase tracking-wide shadow-md hover:bg-[#ff7a20] md:inline-flex"
            >
              DONÁ →
            </a>

            {/* Botón hamburguesa solo en mobile */}
            <button
              type="button"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-white/40 md:hidden"
              aria-label="Abrir menú de navegación"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <span className="sr-only">Toggle navigation</span>
              <div className="space-y-1.5">
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
                <span className="block h-0.5 w-5 bg-white" />
              </div>
            </button>
          </div>
        </div>

        {/* Menú desplegable móvil */}
        {isMenuOpen && (
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 pb-4 text-sm font-medium md:hidden">
            <button
              type="button"
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              onClick={() => {
                setIsMenuOpen(false);
                router.push("/conoce-mas#programa");
              }}
            >
              EL PROGRAMA
            </button>
            <button
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              type="button"
              onClick={() => {
                setIsMenuOpen(false);
                goToMaterials();
              }}
            >
              MATERIALES EDUCATIVOS
            </button>
            <a
              href="#contacto"
              className="w-full rounded-md bg-white/10 px-3 py-2 text-left hover:bg-white/15"
              onClick={() => setIsMenuOpen(false)}
            >
              CONTACTO
            </a>
            <a
              href={DONATION_HEADER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 block w-full rounded-full bg-[#FF8A3D] px-4 py-2 text-center text-xs font-semibold uppercase tracking-wide text-white shadow-md hover:bg-[#ff7a20]"
            >
              DONÁ →
            </a>
          </div>
        )}
      </header>

      {/* Hero */}
      <main
        className="relative mx-auto flex min-h-[30rem] max-w-6xl flex-col gap-10 px-4 py-14 md:min-h-[38rem] md:flex-row md:items-center md:px-6 md:py-20"
        style={{
          backgroundImage: "url('/images/1-FondosWeb-GPEsc.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative flex w-full flex-col gap-10 md:flex-row md:items-center">
          {/* Columna izquierda: video de YouTube */}
          <section className="flex-1 pt-6 md:pt-0">
            <div className="relative mx-auto max-w-lg overflow-hidden rounded-3xl bg-black/30 p-2 shadow-lg backdrop-blur-sm">
              <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
                {heroVideoUrl ? (
                  <iframe
                    src={heroVideoUrl}
                    title="Video hero Greenpeace en las escuelas"
                    className="absolute inset-0 h-full w-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="flex h-full min-h-[14rem] items-center justify-center bg-[#1a1a1a]">
                    <span className="text-sm text-white/70">Cargando video...</span>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Columna derecha: texto y CTA */}
          <section className="flex-1 space-y-6 text-center md:flex md:flex-col md:justify-center md:items-start md:text-left md:pt-4">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8A5A1E]">
              Programa de educación ambiental
            </p>
            <h1 className="text-2xl font-semibold leading-snug text-[#556B2F] md:text-3xl">
              Te damos la bienvenida a un espacio para promover, acompañar y
              potenciar las experiencias y proyectos de educación ambiental en las
              escuelas.
            </h1>
            <Link
              href="/conoce-mas"
              className="mt-2 inline-flex items-center justify-center self-center rounded-full bg-[#3B7C3C] px-6 py-3 text-xs font-semibold uppercase tracking-wide text-white shadow-md hover:bg-[#316834] md:self-start"
            >
              CONOCÉ MÁS
            </Link>
          </section>
        </div>
      </main>

      {/* Sección: ¿Qué vas a encontrar? */}
      <section
        className="mt-0 w-full py-12 text-center text-white"
        style={{
          backgroundImage: "url('/images/2-FondosWeb-GPEsc.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 md:px-6">
          <h2 className="text-2xl font-extrabold uppercase leading-snug md:text-3xl">
            ¿QUÉ VAS A ENCONTRAR EN NUESTRA PLATAFORMA?
          </h2>
          <p className="max-w-3xl text-base leading-relaxed md:text-lg">
            Secuencias didácticas, actividades para el aula, revistas infantiles, videos,
            podcasts, materiales de Lina y los amigos del Arcoíris, calendarios ambientales
            descargables ¡y mucho más!
          </p>

          {/* Ilustración */}
          <div className="mt-4 flex justify-center">
            <Image
              src="/images/Recurso1.png"
              alt="Contenidos de la plataforma Greenpeace en las escuelas"
              width={700}
              height={490}
              className="h-auto w-full max-w-3xl object-contain"
            />
          </div>

          <button
            type="button"
            onClick={goToMaterials}
            className="animate-cta-pulse mt-6 rounded-full bg-[#C6F15A] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-[#1F3B10] shadow-md transition hover:scale-105 hover:bg-[#b7e64e] hover:shadow-lg active:scale-100"
          >
            ACCEDÉ A LOS MATERIALES EDUCATIVOS HACIENDO CLICK ACÁ
          </button>
        </div>
      </section>

      {/* Sección: Carrusel de fotos */}
      <section
        className="w-full pt-23 pb-12"
        style={{
          backgroundImage: "url('/images/3-FondosWeb-GPEsc.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 md:px-10">
          <div className="flex items-center justify-between gap-4">
            {/* Botón previo (desktop) */}
            <button
              type="button"
              aria-label="Foto anterior"
              className="flex h-10 w-10 -translate-x-2 shrink-0 items-center justify-center rounded-full bg-[#BF4BB9] text-white shadow-md hover:bg-[#a13ca0]"
              onClick={() =>
                setCurrentSlide(
                  (prev) =>
                    (prev - 1 + effectiveCarouselImages.length) %
                    (effectiveCarouselImages.length || 1),
                )
              }
            >
              ←
            </button>

            {/* Carrusel: 1 foto en mobile, 3 en desktop */}
            <div className="flex w-full items-center justify-center gap-4">
              {[0, 1, 2].map((offset) => {
                const total = effectiveCarouselImages.length || 1;
                const imageIndex = (currentSlide + offset) % total;

                const isMain = offset === 0;

                return (
                  <div
                    key={imageIndex}
                    className={`relative h-72 flex-1 overflow-hidden rounded-3xl bg-white/70 shadow-lg md:h-96 ${
                      // En mobile solo mostramos la imagen principal
                      isMain ? "block" : "hidden md:block"
                    }`}
                  >
                    {/* Usamos <img> normal para evitar restricciones de Next sobre IPs privadas */}
                    <img
                      src={effectiveCarouselImages[imageIndex]}
                      alt={`Foto ${imageIndex + 1} del programa en las escuelas`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                );
              })}
            </div>

            {/* Botón siguiente (desktop) */}
            <button
              type="button"
              aria-label="Siguiente foto"
              className="flex h-10 w-10 translate-x-2 shrink-0 items-center justify-center rounded-full bg-[#BF4BB9] text-white shadow-md hover:bg-[#a13ca0]"
              onClick={() =>
                setCurrentSlide(
                  (prev) =>
                    (prev + 1) % (effectiveCarouselImages.length || 1),
                )
              }
            >
              →
            </button>
          </div>

          {/* Indicadores de carrusel */}
          <div className="mt-4 flex justify-center gap-2">
            {effectiveCarouselImages.map((_, index) => (
              <button
                key={index}
                type="button"
                aria-label={`Ir a la foto ${index + 1}`}
                className={`h-2 w-2 rounded-full transition ${
                  currentSlide === index ? "bg-[#3B7C3C]" : "bg-[#C5E0A3]"
                }`}
                onClick={() => setCurrentSlide(index)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Sección: Calendario ambiental */}
      <section
        className="w-full py-16"
        style={{
          backgroundImage: "url('/images/4-FondosWeb-GPEsc.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="mx-auto mb-8 flex max-w-6xl justify-center px-4 md:px-6">
          <Image
            src="/images/TituloCalendarioWeb.png"
            alt="Calendario ambiental"
            width={800}
            height={160}
            className="h-auto w-full max-w-3xl object-contain"
          />
        </div>
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="relative flex w-full flex-col items-center">
            {/* Calendario centrado con marco, sin cambiar su tamaño */}
            <div className="w-full">
              <div
                className="relative mx-auto w-full max-w-5xl bg-contain bg-no-repeat px-10 py-10 md:px-16 md:py-16"
                style={{
                  backgroundImage: "url('/images/MarcoCalendario.png')",
                  backgroundPosition: "center 32%",
                }}
              >
                <div className="relative mx-auto w-full max-w-xl rounded-3xl bg-[#F6FFF0] p-6 shadow-xl md:p-8">
                  <div className="mb-6 flex items-center justify-between gap-4">
                    <div className="rounded-full bg-[#FF7F3F] px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-md">
                      {calendarMonthLabel}
                    </div>
                    <div className="flex items-center gap-2 text-xs font-semibold text-[#455A26]">
                      <button
                        type="button"
                        onClick={goToPrevMonth}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#455A26] shadow hover:bg-[#F0F9E7]"
                        aria-label="Mes anterior"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={goToNextMonth}
                        className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#455A26] shadow hover:bg-[#F0F9E7]"
                        aria-label="Mes siguiente"
                      >
                        →
                      </button>
                    </div>
                  </div>

                  {/* Cabecera de días de la semana */}
                  <div className="mb-3 grid grid-cols-7 text-center text-xs font-semibold uppercase tracking-wide text-[#7C3AED]">
                    {["D", "L", "M", "M", "J", "V", "S"].map((label, index) => (
                      <div key={`${label}-${index}`}>{label}</div>
                    ))}
                  </div>

                  {/* Días del mes */}
                  <div className="grid grid-cols-7 gap-y-2 text-sm">
                    {Array.from({ length: firstDayOfWeek }, (_, idx) => (
                      <div key={`blank-${idx}`} />
                    ))}
                    {daysArray.map((day) => {
                      const key = formatKey(displayedYear, displayedMonth, day);
                      const hasEvent = Boolean(eventsByDate[key]);
                      const isToday =
                        clientTodayKey !== null && key === clientTodayKey;
                      const isSelected = key === selectedKey;

                      return (
                        <button
                          key={day}
                          type="button"
                          onClick={() => setSelectedDate(new Date(displayedYear, displayedMonth, day))}
                          className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full border text-sm transition ${
                            isSelected
                              ? "border-[#16A34A] bg-[#BBF7D0] text-[#14532D]"
                              : isToday
                                ? "border-[#7C3AED] bg-white text-[#1F2937]"
                                : "border-transparent bg-white/70 text-[#1F2937] hover:border-[#A7F3D0]"
                          }`}
                        >
                          <span className="relative flex items-center justify-center">
                            {day}
                            {hasEvent && (
                              <span className="absolute -bottom-1 left-1/2 h-1.5 w-1.5 -translate-x-1/2 rounded-full bg-[#16A34A]" />
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  {calendarError && (
                    <p className="mt-3 text-xs text-red-700">
                      {calendarError}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Tarjeta de evento tipo pop-up: solo aparece cuando hay evento seleccionado */}
            {selectedEvent && !calendarLoading && (
              <div className="mt-6 w-full md:mt-0 md:w-auto md:absolute md:top-1/2 md:right-0 md:translate-y-[-50%] md:translate-x-8 lg:translate-x-12">
                <div className="mx-auto max-w-xs rounded-3xl bg-[#0EA5E9] text-white shadow-xl">
                  <div className="relative h-40 w-full overflow-hidden rounded-t-3xl bg-slate-200">
                    {/* Imagen servida por el backend (/uploads/...): URL absoluta al API */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={calendarEventImageSrc(selectedEvent.imageUrl)}
                      alt={selectedEvent.title}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="space-y-3 px-6 pb-6 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#E0F2FE]">
                      {formatCalendarDayHeading(selectedEvent.date)}
                    </p>
                    <h3 className="text-lg font-extrabold leading-snug">
                      {selectedEvent.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-[#E0F2FE]">
                      {selectedEvent.description}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sección: Donación — en desktop el contenido va centrado en la mitad derecha de la sección */}
      <section
        className="min-h-[28rem] w-full bg-left bg-cover pt-16 pb-0 md:min-h-[34rem] md:bg-center md:py-20 bg-[url('/images/fondo-donacion.png')] md:bg-[url('/images/5-FondosWeb-GPEsc.jpg')]"
      >
        <div className="grid w-full grid-cols-1 px-4 md:grid-cols-2 md:min-h-[30rem] md:px-0">
          {/* Mitad izquierda vacía en desktop */}
          <div className="hidden md:block" aria-hidden />
          {/* Mitad derecha: contenido centrado en esta mitad */}
          <div className="flex w-full items-center justify-center px-4 md:px-6 md:py-12">
            <div className="flex max-w-lg flex-col items-center gap-10 text-center text-[#1F2933]">
              <Image
                src="/images/LogoGP.png"
                alt="Greenpeace en las escuelas"
                width={300}
                height={84}
                className="h-auto w-full max-w-[300px] md:max-w-[340px]"
              />
              <h2 className="text-xl font-extrabold uppercase leading-snug text-[#1B5E20] md:text-2xl">
                ¿Te parece importante la educación ambiental?
              </h2>
              <p className="text-base leading-relaxed text-[#2E7D32] md:text-lg">
                Realizando una donación única o mensual nos ayudás a llegar a más escuelas
              </p>
              <a
                href={DONATION_SECTION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-2xl border-2 border-[#81C784] bg-[#2E7D32] px-10 py-3.5 text-base font-bold uppercase tracking-wide text-white shadow-md hover:bg-[#1B5E20] hover:border-[#66BB6A]"
              >
                Doná ahora
              </a>
            </div>
          </div>
        </div>
        {/* Imagen de donación solo en mobile, debajo del bloque principal */}
        <div className="mt-4 block md:hidden">
          <div className="mx-auto flex w-full justify-center px-0">
            <Image
              src="/images/donacion.png"
              alt="Donación Greenpeace en las escuelas"
              width={400}
              height={400}
              className="h-auto w-full max-w-md object-contain"
            />
          </div>
        </div>
      </section>

      {/* Sección: Redes sociales */}
      <section className="w-full bg-[#9B4BB9] pt-0 pb-12 text-center text-white md:py-12">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-8 px-4 md:px-6">
          <h2 className="text-2xl font-semibold md:text-3xl">
            Seguinos en nuestras redes
          </h2>

          {/* Cuenta Greenpeace en las escuelas */}
          <div className="flex flex-col items-center gap-2 text-sm md:text-base">
            <a
              href="https://www.instagram.com/greenpeaceenlasescuelas"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg px-3 py-2 transition hover:opacity-90"
              aria-label="Instagram Greenpeace en las escuelas"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#008f39] text-white">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </span>
              <span className="font-medium">@greenpeaceenlasescuelas</span>
            </a>
          </div>

          {/* Logo Greenpeace + cuenta general */}
          <div className="mt-4 flex flex-col items-center gap-3">
            <Image
              src="/images/gp-logo-blanco.svg"
              alt="Greenpeace"
              width={200}
              height={48}
              className="h-10 w-auto md:h-12"
            />
            <a
              href="https://www.instagram.com/greenpeacearg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-lg px-3 py-2 transition hover:opacity-90"
              aria-label="Instagram Greenpeace Argentina"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#008f39] text-white">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </span>
              <span className="font-medium">@greenpeacearg</span>
            </a>
          </div>
        </div>
      </section>

      {/* Sección: Formulario de acceso 
      <section className="w-full bg-[#E9F8D7] py-12">
        <div className="mx-auto flex max-w-xl justify-center px-4 md:px-0">
          <div className="relative w-full overflow-hidden rounded-[32px] bg-[#2F7A3E] p-3 shadow-lg">
            
            <div className="rounded-[24px] bg-[#F4FCE3] p-6 md:p-8">
              <div className="mb-4 flex justify-center">
                <Image
                  src="/images/LogoGP.png"
                  alt="Greenpeace en las escuelas"
                  width={180}
                  height={50}
                />
              </div>

              <h2 className="text-center text-sm font-extrabold uppercase tracking-[0.18em] text-[#2F7A3E] md:text-base">
                ¡UNITE A GREENPEACE EN LAS ESCUELAS!
              </h2>
              <p className="mt-3 text-center text-xs leading-relaxed text-[#334155] md:text-sm">
                Registrate en la plataforma para acceder a todos los materiales y contenidos.
                Además vas a recibir el newsletter con efemérides y novedades ambientales.
              </p>

              <form
                className="mt-6 space-y-4 text-left"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setFormError(null);
                  if (!formEmail.trim() || !formPassword) {
                    setFormError("Ingresá tu correo y contraseña.");
                    return;
                  }
                  setFormLoading(true);
                  try {
                    const res = await fetch(`${getApiBaseUrl()}/auth/login`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({
                        email: formEmail.trim().toLowerCase(),
                        password: formPassword,
                      }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      setFormError(data.error ?? "Error al iniciar sesión.");
                      return;
                    }
                    if (typeof window !== "undefined") {
                      localStorage.setItem("gp_token", data.token);
                      localStorage.setItem("gp_user", JSON.stringify(data.user));
                    }
                    router.push("/materiales-educativos");
                  } catch {
                    setFormError("Error de conexión. Intentá de nuevo.");
                  } finally {
                    setFormLoading(false);
                  }
                }}
              >
                <div className="space-y-1">
                  <label
                    htmlFor="home-email"
                    className="text-xs font-semibold text-[#1F2933]"
                  >
                    Correo electrónico
                  </label>
                  <input
                    id="home-email"
                    type="email"
                    value={formEmail}
                    onChange={(e) => {
                      setFormEmail(e.target.value);
                      setFormError(null);
                    }}
                    className="w-full rounded-md border border-[#C0CFB2] bg-white px-3 py-2 text-sm text-[#1F2933] outline-none ring-0 focus:border-[#2BB5D7]"
                    placeholder="tu-correo@ejemplo.com"
                    disabled={formLoading}
                  />
                </div>

                <div className="space-y-1">
                  <label
                    htmlFor="home-password"
                    className="text-xs font-semibold text-[#1F2933]"
                  >
                    Contraseña
                  </label>
                  <input
                    id="home-password"
                    type="password"
                    value={formPassword}
                    onChange={(e) => {
                      setFormPassword(e.target.value);
                      setFormError(null);
                    }}
                    className="w-full rounded-md border border-[#C0CFB2] bg-white px-3 py-2 text-sm text-[#1F2933] outline-none ring-0 focus:border-[#2BB5D7]"
                    placeholder="••••••••"
                    disabled={formLoading}
                  />
                </div>

                {formError && (
                  <p className="text-xs text-red-600">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={formLoading}
                  className="mt-2 w-full rounded-md bg-[#11B3D8] py-2 text-sm font-semibold text-white shadow-md hover:bg-[#0fa0c1] disabled:opacity-70"
                >
                  {formLoading ? "Iniciando sesión..." : "Iniciar sesión"}
                </button>
              </form>

              <div className="mt-4 space-y-1 text-center text-[11px] text-[#4B5563] md:text-xs">
                <button
                  type="button"
                  onClick={() => router.push("/recuperar-contrasena")}
                  className="block w-full text-[#1F2933] hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </button>
                <button
                  type="button"
                  onClick={() => goToLoginWithMode("login")}
                  className="block w-full text-[#1F2933] hover:underline"
                >
                  ¿No recibiste el email de activación?
                </button>
                <button
                  type="button"
                  onClick={() => goToLoginWithMode("register")}
                  className="mt-1 block w-full font-semibold text-[#1F2933] hover:underline"
                >
                  Registrarse
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      */}
      
      {/* Footer: fondo verde lima con degradado oscuro en la base. Guardá tu imagen en public/images/footer-bg.png para usarla de fondo. */}
      <footer
        id="contacto"
        className="relative bg-cover bg-center bg-no-repeat text-[#1F2933]"
        style={{
          backgroundColor: "#9ACD32",
          backgroundImage: "linear-gradient(to top, rgba(30,30,30,0.22) 0%, transparent 22%)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative mx-auto max-w-6xl px-4 py-10 md:px-6 md:py-12">
          <div className="grid gap-8 md:grid-cols-2 md:gap-12 md:items-center">
            <div className="flex flex-col items-start gap-4 md:order-2">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#1F2933]">
                Contacto
              </h3>
              <div className="h-px w-16 bg-[#1F2933]/40" aria-hidden />
              <p className="text-sm leading-relaxed text-[#1F2933]/95">
                Para contactarte con nosotros escribinos por las siguientes vías de comunicación:
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href="https://wa.me/5491171206317"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </span>
                    +54 9 11 7120 6317
                  </a>
                </li>
                <li>
                  <a
                    href="mailto:escuelas@greenpeace.org.ar"
                    className="inline-flex items-center gap-2 font-medium text-[#1F2933] hover:underline"
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden>
                      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                    </span>
                    escuelas@greenpeace.org.ar
                  </a>
                </li>
              </ul>
            </div>
            <div className="flex flex-col items-center gap-4 md:order-1 md:items-start md:justify-center">
              <a
                href="https://www.greenpeace.org/argentina/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2933]/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#9ACD32]"
                aria-label="Fundación Greenpeace Argentina (sitio web)"
              >
                <Image
                  src="/images/gp-logo-blanco.svg"
                  alt="Greenpeace Argentina"
                  width={160}
                  height={48}
                  className="h-auto w-36 object-contain md:w-40"
                />
              </a>
              <p className="text-xs text-[#1F2933]/90">
                © 2026 Greenpeace Argentina. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
